import {
    ApartmentOutlined,
    CheckCircleOutlined,
    EditOutlined,
    InboxOutlined,
    PlusOutlined,
    RiseOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    notification,
    Popconfirm,
    Skeleton,
    Upload,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import { decrypt, formatDate, getBase64 } from "../../services/util";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";
import "./Rooms.css";
dayjs.extend(customParseFormat);

const { Dragger } = Upload;

function RoomsAvailable() {
    const [form] = Form.useForm();
    const [fileForm] = Form.useForm();

    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [selectedDate, setSelectedDate] = useState(
        dayjs().subtract(1, "day"),
    );
    let fileObj = null;
    const [uploadDate, setUploadDate] = useState(null);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [formData, setFromData] = useState({
        staff_id,
        trans_date: formatDate(selectedDate),
    });
    const [tableData, setTableData] = useState([]);
    const [editingKey, setEditingKey] = useState("");

    const neutralActionButtonStyle = {
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: 7,
        width: 30,
        height: 30,
    };

    const metricPillStyle = (type = "muted") => {
        const styles = {
            accent: {
                background: "var(--color-accent-light)",
                color: "var(--color-accent)",
            },
            primary: {
                background: "var(--color-primary-light)",
                color: "var(--color-primary)",
            },
            muted: {
                background: "var(--color-bg-elevated)",
                color: "var(--color-text-secondary)",
            },
        };

        return {
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 10px",
            borderRadius: "999px",
            border: "1px solid var(--color-border)",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.4,
            ...styles[type],
        };
    };

    const emptyText = (label = "Not available") => (
        <span style={{ color: "var(--color-text-muted)", fontSize: 12 }}>
            {label}
        </span>
    );

    const renderHotelIdentity = (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 10,
                    background: "var(--color-primary-light)",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                <ApartmentOutlined style={{ fontSize: 14 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span
                    style={{
                        color: "var(--color-text)",
                        fontSize: 13,
                        fontWeight: 600,
                    }}
                >
                    {record.hotel_name || "Unnamed hotel"}
                </span>
                <span
                    style={{
                        color: "var(--color-text-muted)",
                        fontSize: 11,
                    }}
                >
                    Hotel ID {record.hotel_id}
                </span>
            </div>
        </div>
    );

    const renderTextCell = (value, emptyLabel = "Not available") =>
        value || value === 0 ? (
            <span style={{ color: "var(--color-text)", fontSize: 13 }}>
                {value}
            </span>
        ) : (
            emptyText(emptyLabel)
        );

    const renderMetricChip = (value, type = "muted", emptyLabel = "No data") =>
        value || value === 0 ? (
            <span style={metricPillStyle(type)}>{value}</span>
        ) : (
            emptyText(emptyLabel)
        );

    const renderPercentageChip = (
        value,
        type = "accent",
        emptyLabel = "Not available",
    ) =>
        value || value === 0 ? (
            <span style={metricPillStyle(type)}>
                <RiseOutlined style={{ fontSize: 11 }} />
                {value}%
            </span>
        ) : (
            emptyText(emptyLabel)
        );

    const columns = [
        {
            title: "Hotel Id",
            dataIndex: "hotel_id",
            key: "hotel_id",
            render: (value) => renderTextCell(value, "No hotel ID"),
        },
        {
            title: "Hotel Name",
            dataIndex: "hotel_name",
            key: "hotel_name",
            render: (_, record) => renderHotelIdentity(_, record),
        },
        {
            title: "City",
            dataIndex: "city",
            key: "city",
            render: (value) => renderTextCell(value, "City not added"),
        },
        {
            title: "Rooms Sold",
            dataIndex: "rooms_sold",
            key: "rooms_sold",
            editable: true,
            render: (value) =>
                renderMetricChip(value, "primary", "No rooms sold"),
        },
        {
            title: "Out Of Order",
            dataIndex: "out_of_order",
            key: "out_of_order",
            editable: true,
            render: (value) =>
                renderMetricChip(value, "muted", "No out-of-order rooms"),
        },
        {
            title: "Adoption (%)",
            dataIndex: "adopt_percentage",
            key: "adopt_percentage",
            render: (value) =>
                renderPercentageChip(value, "accent", "No adoption data"),
        },
        {
            title: "Occupancy (%)",
            dataIndex: "occ_percentage",
            key: "occ_percentage",
            editable: true,
            render: (value) =>
                renderPercentageChip(value, "primary", "No occupancy data"),
        },
        {
            title: "Action",
            key: "action",
            width: 96,
            render: (_, record) => {
                const editable = isEditing(record);
                return editable ? (
                    <div className="rooms-action-inline">
                        <Button
                            type="primary"
                            size="small"
                            icon={<CheckCircleOutlined />}
                            onClick={() => save(record.hotel_id)}
                        >
                            Save
                        </Button>
                        <Popconfirm title="Discard changes?" onConfirm={cancel}>
                            <Button size="small">Cancel</Button>
                        </Popconfirm>
                    </div>
                ) : (
                    <Button
                        type="text"
                        size="small"
                        disabled={editingKey !== ""}
                        icon={
                            <EditOutlined
                                style={{
                                    color:
                                        editingKey !== ""
                                            ? "var(--color-text-muted)"
                                            : "var(--color-primary)",
                                }}
                            />
                        }
                        onClick={() => edit(record)}
                        style={neutralActionButtonStyle}
                    />
                );
            },
        },
    ];

    const {
        status: roomDetailStatus,
        data: roomDetailsRes,
        refetch: getRoomDetails,
    } = useQuery(
        "rooms-details",
        async () => {
            return HTTP("/rooms/getRoomsSold", formData);
        },
        { enabled: false },
    );

    const { data: tokenData, refetch: getToken } = useQuery(
        "randomToken",
        async () => {
            return await HTTP("/dashboard/getRandomToken", { staff_id });
        },
        {
            enabled: false,
            select: (data) => {
                if (data.success && Array.isArray(data.data)) {
                    return decrypt(data.data[0]?.random_string);
                } else {
                    return null;
                }
            },
        },
    );

    useEffect(() => {
        form.setFieldsValue({ trans_date: dayjs().subtract(1, "day") });
        getRoomDetails();
        getToken();
    }, []);

    useEffect(() => {
        getRoomDetails();
    }, [formData]);

    useEffect(() => {
        if (roomDetailStatus === "success") {
            setTableData(roomDetailsRes.data);
        }
    }, [roomDetailStatus, roomDetailsRes]);

    function disabledDate(current) {
        return current && current > dayjs().subtract(1, "day").endOf("day");
    }

    function openUploadPopup() {
        setUploadModalVisible(true);
    }

    function handleUploadModalClose() {
        fileObj = null;
        fileForm.resetFields();
        setUploadDate(null);
        setUploadModalVisible(false);
        getRoomDetails();
    }

    function edit(record) {
        form.setFieldsValue({
            ...record,
        });
        setEditingKey(record.hotel_id);
    }

    function isEditing(record) {
        return record.hotel_id === editingKey;
    }

    function cancel() {
        setEditingKey("");
    }

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record) => ({
                record,
                inputType: col.dataIndex === "age" ? "number" : "text",
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    function handleDateChange(date, dateString) {
        setSelectedDate(dayjs(dateString));
        setFromData({ ...formData, trans_date: formatDate(dateString) });
    }

    const EditableCell = ({
        editing,
        dataIndex,
        title,
        inputType,
        record,
        index,
        children,
        ...restProps
    }) => {
        const inputNode = inputType === "number" ? <InputNumber /> : <Input />;
        return (
            <td {...restProps}>
                {editing ? (
                    <Form.Item
                        name={dataIndex}
                        style={{
                            margin: 0,
                        }}
                        rules={[
                            {
                                required: true,
                                message: `Please Input ${title}!`,
                            },
                        ]}
                    >
                        {inputNode}
                    </Form.Item>
                ) : (
                    children
                )}
            </td>
        );
    };

    async function save(hotel_id) {
        const row = { ...(await form.validateFields()), hotel_id };
        const newData = [...roomDetailsRes.data];
        const index = newData.findIndex((item) => hotel_id === item.hotel_id);
        const { success, message } = await HTTP("/rooms/addRoomsSold", {
            staff_id,
            rooms_details: [row],
            trans_date: formatDate(selectedDate),
        });

        if (success === 0) {
            notification["error"]({
                message: message,
            });
            setEditingKey("");
        } else {
            notification["info"]({
                message: message,
            });
            const item = newData[index];
            newData.splice(index, 1, { ...item, ...{ ...row, hotel_id } });
            setTableData(newData);
            setEditingKey("");
        }
    }

    async function uploadDetails() {
        fileForm.validateFields().then((values) => {
            getBase64(fileObj.file.originFileObj, async (dataUrl) => {
                const { success, data, message } = await HTTP(
                    "/rooms/reportUploadFileNew",
                    {
                        staff_id,
                        trans_date: formatDate(uploadDate),
                        file_path: dataUrl,
                        file_name: fileObj.file.name,
                    },
                );
                let notification_type = "info";
                if (success === 0) {
                    notification_type = "error";
                }
                notification[notification_type]({
                    message: message,
                });
                handleUploadModalClose();
            });
        });
    }

    function handleFileUpload(info) {
        fileObj = info;
    }

    return (
        <Card className="dashboard-container">
            <CustomHeader />
            <Card
                className="box-shadow margin-10"
                title={
                    <div className="display-flex flex-space-between">
                        <div
                            style={{ alignItems: "center" }}
                            className="display-flex"
                        >
                            <span>{`Token : ${tokenData || ""}`}</span>
                        </div>
                        <div>
                            <DatePicker
                                allowClear={false}
                                className="margin-10"
                                defaultValue={dayjs().subtract(1, "day")}
                                value={selectedDate}
                                onChange={handleDateChange}
                                format={"MM/DD/YYYY"}
                                disabledDate={disabledDate}
                                showToday={false}
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openUploadPopup}
                            >
                                Upload Room Details
                            </Button>
                        </div>
                    </div>
                }
            >
                <Form form={form} component={false}>
                    {roomDetailStatus != "idle" &&
                    roomDetailStatus != "loading" ? (
                        <CustomTable
                            components={{
                                body: {
                                    cell: EditableCell,
                                },
                            }}
                            rowKey="hotel_id"
                            columns={mergedColumns}
                            tableData={tableData}
                        />
                    ) : (
                        <Skeleton
                            active={true}
                            title={false}
                            paragraph={{ rows: 10 }}
                        />
                    )}
                </Form>
            </Card>
            <Modal
                open={uploadModalVisible}
                className="rooms-upload-modal"
                width={680}
                footer={
                    <div className="rooms-upload-footer">
                        <Button onClick={handleUploadModalClose}>Cancel</Button>
                        <Button type="primary" onClick={uploadDetails}>
                            Submit
                        </Button>
                    </div>
                }
                title={
                    <div className="rooms-upload-header">
                        <div className="rooms-upload-title">
                            Upload Room Details
                        </div>
                        <div className="rooms-upload-subtitle">
                            Upload a room status file for a specific reporting
                            date.
                        </div>
                    </div>
                }
                destroyOnHidden
                onCancel={handleUploadModalClose}
            >
                <Form
                    form={fileForm}
                    layout="vertical"
                    className="rooms-upload-form"
                >
                    <div className="rooms-upload-section">
                        <div className="rooms-upload-section-title">
                            Reporting Date
                        </div>
                        <Form.Item
                            label="Select date"
                            name="Date"
                            rules={[
                                {
                                    required: true,
                                    message: "Please select a date",
                                },
                            ]}
                        >
                            <DatePicker
                                allowClear={false}
                                value={uploadDate}
                                format={"MM/DD/YYYY"}
                                onChange={(date) => {
                                    setUploadDate(date);
                                }}
                                disabledDate={(current) => {
                                    return (
                                        current &&
                                        current > dayjs().subtract(1, "day")
                                    );
                                }}
                                className="rooms-upload-date"
                            />
                        </Form.Item>
                    </div>
                    <div className="rooms-upload-section">
                        <div className="rooms-upload-section-title">
                            Upload File
                        </div>
                        <Form.Item
                            label="Room details file"
                            name="file"
                            valuePropName="fileList"
                            getValueFromEvent={(event) =>
                                Array.isArray(event) ? event : event?.fileList
                            }
                            rules={[
                                {
                                    required: true,
                                    message: "Please upload a CSV or XLSX file",
                                },
                            ]}
                        >
                            <Dragger
                                className="rooms-upload-dragger"
                                beforeUpload={() => false}
                                id="file"
                                name="file"
                                onChange={(info) => handleFileUpload(info)}
                                accept=".csv, .xlsx"
                                maxCount={1}
                            >
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">
                                    Click or drag a file here to upload
                                </p>
                                <p className="ant-upload-hint">
                                    Supported formats: CSV and XLSX
                                </p>
                            </Dragger>
                        </Form.Item>
                    </div>
                </Form>
            </Modal>
        </Card>
    );
}
export { RoomsAvailable };
